import { FC, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import ModalForm from "../../../components/ModalForm";
import { updateUserRole } from "../../../services/api/admin/role";
import { Role } from "../../../types/enums/Role";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    notifyFieldErrors,
} from "../../../utils/errors";
import { isStaffRole } from "../../../utils/graduation";

type RoleOption = { id: string; name: string };

type Props = {
    open: boolean;
    handleClose: () => void;
    userId: string | null;
    userDisplayName?: string;
    currentRoleName?: string | null;
    roles: RoleOption[];
    actorRoleName?: string | null;
    onSuccess?: () => void;
};

const STAFF_ROLE_ORDER = [Role.Formateur, Role.Admin, Role.SuperAdmin] as const;

const PromoteToStaffModal: FC<Props> = ({
    open,
    handleClose,
    userId,
    userDisplayName,
    currentRoleName,
    roles,
    actorRoleName,
    onSuccess,
}) => {
    const [roleId, setRoleId] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    const actorIsSuperAdmin = actorRoleName === Role.SuperAdmin;

    const staffOptions = useMemo(() => {
        const byName = new Map(roles.map((role) => [role.name, role]));
        return STAFF_ROLE_ORDER.filter((name) => {
            if (name === Role.SuperAdmin && !actorIsSuperAdmin) return false;
            return byName.has(name);
        }).map((name) => {
            const role = byName.get(name)!;
            return { value: role.id, label: role.name };
        });
    }, [roles, actorIsSuperAdmin]);

    useEffect(() => {
        if (!open) return;
        const currentStaff = roles.find(
            (role) =>
                role.name === currentRoleName && isStaffRole(role.name)
        );
        const preferred =
            currentStaff &&
            (currentStaff.name !== Role.SuperAdmin || actorIsSuperAdmin)
                ? currentStaff.id
                : staffOptions[0]?.value ?? "";
        setRoleId(preferred);
        setFieldErrors({});
    }, [open, roles, currentRoleName, staffOptions, actorIsSuperAdmin]);

    const handleSubmit = async () => {
        if (!userId) return;
        const selected = roles.find((role) => role.id === roleId);
        if (!selected || !isStaffRole(selected.name)) {
            const staffErrors = { staffRoleId: REQUIRED_FIELD };
            setFieldErrors(staffErrors);
            notifyFieldErrors(staffErrors, {
                message: "Veuillez sélectionner un rôle staff.",
                type: "error",
            });
            return;
        }
        if (selected.name === Role.SuperAdmin && !actorIsSuperAdmin) {
            const staffErrors = {
                staffRoleId:
                    "Seul un Super Admin peut attribuer le rôle Super Admin.",
            };
            setFieldErrors(staffErrors);
            notifyFieldErrors(staffErrors, {
                message: staffErrors.staffRoleId,
                type: "error",
            });
            return;
        }
        setFieldErrors({});

        setSubmitting(true);
        try {
            await updateUserRole(userId, selected.name);
            toast.success(`Rôle staff « ${selected.name} » attribué.`);
            onSuccess?.();
            handleClose();
        } catch {
            // L'intercepteur apiService affiche déjà le toast d'erreur API.
        } finally {
            setSubmitting(false);
        }
    };

    const title = userDisplayName
        ? `Promouvoir en staff — ${userDisplayName}`
        : "Promouvoir en staff";

    return (
        <ModalForm
            open={open}
            handleClose={handleClose}
            title={title}
            size="small"
            isLoading={submitting}
            fields={[
                {
                    id: "staffRoleId",
                    label: "Rôle staff",
                    type: "select",
                    value: roleId,
                    onChange: (value) => {
                        setRoleId(value as string);
                        setFieldErrors((prev) =>
                            clearFieldError(prev, "staffRoleId")
                        );
                    },
                    options: staffOptions,
                    error: fieldErrors.staffRoleId,
                },
            ]}
            onSubmit={handleSubmit}
        />
    );
};

export default PromoteToStaffModal;
